import React, { useMemo } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { FieldValues } from 'react-hook-form';
import {
  SelectWithController,
  SelectWithControllerProperties,
} from '@fuww/library/src/SelectMenus';
import withQuery from '../lib/withQuery';
import countriesQuery from '../lib/queries/countries.graphql';

const countrySelectWithoutQueryPropertyTypes = {
  data: PropTypes.shape({
    countries: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired).isRequired,
  }).isRequired,
};

type CountrySelectWithoutQueryProperties<
  T extends FieldValues = FieldValues,
> = InferProps<
  typeof countrySelectWithoutQueryPropertyTypes
> & Omit<SelectWithControllerProperties<T>, 'options'> & {
  emptyLabel?: string;
};

const CountrySelectWithoutQuery = <T extends FieldValues = FieldValues>({
  data,
  emptyLabel,
  ...rest
}: CountrySelectWithoutQueryProperties<T>) => {
  const countryOptions = useMemo(() => [
    { label: emptyLabel ?? '', value: '' },
    ...data.countries.map(({ name }) => ({
      label: name,
      value: name,
    })),
  ], [data, emptyLabel]);

  return (
    <SelectWithController
      options={countryOptions}
      {...rest}
    />
  );
};

CountrySelectWithoutQuery.propTypes = countrySelectWithoutQueryPropertyTypes;

const CountrySelectWithQuery = withQuery(CountrySelectWithoutQuery);

const CountrySelect = <T extends FieldValues = FieldValues>(
  properties: Omit<CountrySelectWithoutQueryProperties<T>, 'data'>,
) => (
  <CountrySelectWithQuery
    {...properties}
    query={countriesQuery}
    fetchPolicy="cache-first"
    errorMessage="Failed to load countries"
  />
  );

export default CountrySelect;
